export const DAY_TOKENS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const DAY_TOKEN_LABELS = {
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
};

export const DEFAULT_AVAILABILITY = {
    allDay: false,
    schedule: [
        {
            days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
            blocks: [{ start: '21:00', end: '08:00' }],
        },
    ],
};

export const DEFAULT_SCHEDULE_BLOCK = {
    days: [...DAY_TOKENS],
    blocks: [{ start: '21:00', end: '08:00' }],
};

/** Hour options aligned with EditPropertyAvailabilityPage (24h storage, AM/PM display). */
export const TIME_OPTIONS = [
    { display: '12:00 am', value: '00:00' },
    { display: '1:00 am', value: '01:00' },
    { display: '2:00 am', value: '02:00' },
    { display: '3:00 am', value: '03:00' },
    { display: '4:00 am', value: '04:00' },
    { display: '5:00 am', value: '05:00' },
    { display: '6:00 am', value: '06:00' },
    { display: '7:00 am', value: '07:00' },
    { display: '8:00 am', value: '08:00' },
    { display: '9:00 am', value: '09:00' },
    { display: '10:00 am', value: '10:00' },
    { display: '11:00 am', value: '11:00' },
    { display: '12:00 pm', value: '12:00' },
    { display: '1:00 pm', value: '13:00' },
    { display: '2:00 pm', value: '14:00' },
    { display: '3:00 pm', value: '15:00' },
    { display: '4:00 pm', value: '16:00' },
    { display: '5:00 pm', value: '17:00' },
    { display: '6:00 pm', value: '18:00' },
    { display: '7:00 pm', value: '19:00' },
    { display: '8:00 pm', value: '20:00' },
    { display: '9:00 pm', value: '21:00' },
    { display: '10:00 pm', value: '22:00' },
    { display: '11:00 pm', value: '23:00' },
];

export function formatTimeDisplay(value) {
    const match = TIME_OPTIONS.find((opt) => opt.value === value);
    return match?.display ?? value;
}

function compactAmPmFromDisplay(display) {
    if (typeof display !== 'string') return display;
    const match = /^(\d{1,2}):(\d{2})\s*(am|pm)$/i.exec(display.trim());
    if (!match) return display;
    const hour = parseInt(match[1], 10);
    const minute = match[2];
    const period = match[3].toUpperCase();
    if (minute === '00') return `${hour} ${period}`;
    return `${hour}:${minute} ${period}`;
}

export function formatCompactTime(value) {
    if (!value) return value;
    const fromOptions = TIME_OPTIONS.find((opt) => opt.value === value);
    if (fromOptions) return compactAmPmFromDisplay(fromOptions.display);

    const match = /^(\d{1,2}):(\d{2})$/.exec(String(value));
    if (!match) return value;
    const hour24 = parseInt(match[1], 10);
    const minute = match[2];
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 || 12;
    if (minute === '00') return `${hour12} ${period}`;
    return `${hour12}:${minute} ${period}`;
}

function formatAvailabilityBlock(block) {
    return `${formatCompactTime(block.start)}–${formatCompactTime(block.end)}`;
}

function collapseDays(days) {
    const sorted = [...new Set(
        days
            .map((day) => String(day).toLowerCase())
            .filter((day) => DAY_TOKENS.includes(day)),
    )].sort((a, b) => DAY_TOKENS.indexOf(a) - DAY_TOKENS.indexOf(b));

    if (sorted.length === 0) return '';
    if (sorted.length === 7) return 'Every day';

    const runs = [];
    let runStart = 0;
    for (let i = 1; i <= sorted.length; i += 1) {
        const prevIdx = DAY_TOKENS.indexOf(sorted[i - 1]);
        const currIdx = i < sorted.length ? DAY_TOKENS.indexOf(sorted[i]) : -1;
        if (i === sorted.length || currIdx !== prevIdx + 1) {
            runs.push(sorted.slice(runStart, i));
            runStart = i;
        }
    }

    return runs.map((run) => {
        if (run.length === 1) return DAY_TOKEN_LABELS[run[0]];
        return `${DAY_TOKEN_LABELS[run[0]]}–${DAY_TOKEN_LABELS[run[run.length - 1]]}`;
    }).join(', ');
}

export function normalizeScheduleArray(value) {
    if (!Array.isArray(value)) return [];
    return value
        .filter((entry) => entry && Array.isArray(entry.days) && Array.isArray(entry.blocks))
        .map((entry) => ({
            // Lowercase day tokens at the normalization boundary. The editor's
            // membership check (`entry.days.includes(day)` where `day` comes
            // from lowercase `DAY_TOKENS`) and the chip-render guard both
            // expect canonical lowercase. Without this, a legacy entry stored
            // as 'Mon' renders the chip unchecked, and a click would append
            // 'mon' to produce ['Mon', 'mon'] — a within-block duplicate that
            // sidesteps every duplicate-day guard the editor relies on.
            days: entry.days.map((d) => String(d).toLowerCase()),
            blocks: entry.blocks
                .filter((b) => b && b.start && b.end)
                .map((b) => ({ start: b.start, end: b.end })),
        }))
        .filter((entry) => entry.blocks.length > 0);
}

/**
 * Accepts { allDay, schedule } or a legacy bare schedule array.
 */
export function normalizeAvailability(value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return {
            allDay: Boolean(value.allDay),
            schedule: normalizeScheduleArray(value.schedule),
        };
    }
    if (Array.isArray(value)) {
        return { allDay: false, schedule: normalizeScheduleArray(value) };
    }
    return { allDay: false, schedule: [] };
}

export function hasStoredAvailability(value) {
    const { allDay, schedule } = normalizeAvailability(value);
    return allDay || schedule.length > 0;
}

export function formatAvailabilitySummary(availability) {
    const { allDay, schedule } = normalizeAvailability(availability);
    if (allDay) return ['Open 24/7'];

    const normalized = schedule;
    if (!normalized.length) return [];

    return normalized
        .filter((entry) => entry.days.length > 0 && entry.blocks.length > 0)
        .map((entry) => {
            const daysLabel = collapseDays(entry.days);
            if (!daysLabel) return null;
            const blocksLabel = entry.blocks.map(formatAvailabilityBlock).join(', ');
            return `${daysLabel}: ${blocksLabel}`;
        })
        .filter(Boolean);
}

export function createEmptyScheduleBlock() {
    return {
        days: [],
        blocks: [{ start: '09:00', end: '17:00' }],
    };
}

export function isOvernightBlock(block) {
    if (!block?.start || !block?.end) return false;
    return block.end <= block.start;
}

const MINUTES_PER_DAY = 1440;

function parseTimeToMinutes(time) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(String(time));
    if (!match) return null;
    return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

export function availabilityRangeKey(entryIndex, blockIndex) {
    return `${entryIndex}:${blockIndex}`;
}

/**
 * Total hours the schedule is open across one week.
 *   - allDay=true  → 168 (24 × 7)
 *   - empty       → 0
 *   - normal      → sum(blockDuration × days.length) for every block
 * Overnight blocks (end <= start) are treated as (24h - start) + end.
 * A start==end block is treated as full 24h coverage (mirrors
 * expandBlockIntervals).
 */
export function weeklyAvailableHours(availability) {
    const { allDay, schedule } = normalizeAvailability(availability);
    if (allDay) return 24 * 7;
    if (!schedule.length) return 0;

    let totalMinutes = 0;
    for (const entry of schedule) {
        const dayCount = Array.isArray(entry.days) ? entry.days.length : 0;
        if (dayCount === 0) continue;
        for (const block of entry.blocks ?? []) {
            const startMin = parseTimeToMinutes(block.start);
            const endMin = parseTimeToMinutes(block.end);
            if (startMin == null || endMin == null) continue;
            let duration;
            if (startMin === endMin) {
                duration = MINUTES_PER_DAY;
            } else if (endMin > startMin) {
                duration = endMin - startMin;
            } else {
                duration = (MINUTES_PER_DAY - startMin) + endMin;
            }
            totalMinutes += duration * dayCount;
        }
    }
    return totalMinutes / 60;
}

/**
 * Availability hours normalized to a month. Uses 52 weeks / 12 months
 * so a stable 168h/week (24×7) rolls out to ~728 h/mo.
 */
export function monthlyAvailableHours(availability) {
    return weeklyAvailableHours(availability) * 52 / 12;
}

/**
 * Expand a block on a selected day token into half-open [start, end) minute intervals per calendar day.
 * Overnight (end <= start): [start, 1440) on D plus [0, end) on D+1. start === end → full 24h on D.
 */
function expandBlockIntervals(block, dayToken) {
    const startMin = parseTimeToMinutes(block.start);
    const endMin = parseTimeToMinutes(block.end);
    if (startMin == null || endMin == null) return [];

    const dayIdx = DAY_TOKENS.indexOf(dayToken);
    if (dayIdx === -1) return [];

    if (startMin === endMin) {
        return [{ day: dayToken, start: 0, end: MINUTES_PER_DAY }];
    }

    if (endMin > startMin) {
        return [{ day: dayToken, start: startMin, end: endMin }];
    }

    const intervals = [{ day: dayToken, start: startMin, end: MINUTES_PER_DAY }];
    if (endMin > 0) {
        intervals.push({
            day: DAY_TOKENS[(dayIdx + 1) % DAY_TOKENS.length],
            start: 0,
            end: endMin,
        });
    }
    return intervals;
}

function intervalsStrictlyOverlap(aStart, aEnd, bStart, bEnd) {
    return aStart < bEnd && bStart < aEnd;
}

/**
 * Returns keys "<entryIndex>:<blockIndex>" for every range participating in any overlap.
 */
export function findOverlappingRanges(value) {
    const overlapping = new Set();
    const { allDay, schedule } = normalizeAvailability(value);

    if (allDay || schedule.length === 0) {
        return overlapping;
    }

    const byDay = new Map(DAY_TOKENS.map((day) => [day, []]));

    schedule.forEach((entry, entryIndex) => {
        if (!entry?.days?.length || !entry?.blocks?.length) return;

        entry.blocks.forEach((block, blockIndex) => {
            if (!block?.start || !block?.end) return;
            const key = availabilityRangeKey(entryIndex, blockIndex);

            for (const day of entry.days) {
                const token = String(day).toLowerCase();
                if (!DAY_TOKENS.includes(token)) continue;

                for (const interval of expandBlockIntervals(block, token)) {
                    byDay.get(interval.day)?.push({
                        key,
                        start: interval.start,
                        end: interval.end,
                    });
                }
            }
        });
    });

    for (const day of DAY_TOKENS) {
        const intervals = byDay.get(day) || [];
        for (let i = 0; i < intervals.length; i += 1) {
            for (let j = i + 1; j < intervals.length; j += 1) {
                const a = intervals[i];
                const b = intervals[j];
                if (intervalsStrictlyOverlap(a.start, a.end, b.start, b.end)) {
                    overlapping.add(a.key);
                    overlapping.add(b.key);
                }
            }
        }
    }

    return overlapping;
}

// Collect the set of day tokens claimed by EVERY entry other than
// `entryIndex`. The editor uses this to grey out chips for days that
// are already taken by a sibling block, so a host can't put e.g.
// Saturday in two schedules with different time ranges.
//
// Lowercases each token before the DAY_TOKENS membership check so a
// stored entry containing a capitalized variant ('Mon', 'TUE') is still
// counted — matching findOverlappingRanges' lowercase normalization.
// Without this, the overlap detector and the claim tracker would
// disagree on which days are duplicates.
export function daysClaimedByOtherEntries(value, entryIndex) {
    const { schedule } = normalizeAvailability(value);
    const claimed = new Set();
    schedule.forEach((entry, i) => {
        if (i === entryIndex) return;
        for (const day of entry.days ?? []) {
            const token = String(day).toLowerCase();
            if (DAY_TOKENS.includes(token)) claimed.add(token);
        }
    });
    return claimed;
}

// True iff every weekday already lives in some entry. Drives the
// "+ Add schedule block" affordance: when all 7 days are claimed,
// there's no room for another block (would have to be empty), so the
// add button is hidden. Same case-normalization as daysClaimedByOtherEntries.
export function allDaysClaimed(value) {
    const { schedule } = normalizeAvailability(value);
    const claimed = new Set();
    for (const entry of schedule) {
        for (const day of entry.days ?? []) {
            const token = String(day).toLowerCase();
            if (DAY_TOKENS.includes(token)) claimed.add(token);
        }
    }
    return claimed.size === DAY_TOKENS.length;
}

export function hasAvailabilityOverlap(value) {
    return findOverlappingRanges(value).size > 0;
}

/** @deprecated Prefer normalizeAvailability; returns schedule array only. */
export function normalizeScheduleBlocks(value) {
    return normalizeAvailability(value).schedule;
}

export function cloneDefaultAvailability() {
    return normalizeAvailability(DEFAULT_AVAILABILITY);
}

export function resolveListingAvailability(listing) {
    const confirmed = listing?.confirmedFields?.availability;
    if (hasStoredAvailability(confirmed)) {
        return normalizeAvailability(confirmed);
    }

    const enrichment = listing?.enrichmentFields?.availability;
    const enrichVal = enrichment?.value ?? enrichment;
    if (hasStoredAvailability(enrichVal)) {
        return normalizeAvailability(enrichVal);
    }

    return cloneDefaultAvailability();
}

// PP-321 — driver-facing property page helpers.
// The server stores availability as Availability.recurringDays:
//   [{ dayOfWeek: 'Monday', timeSlots: [{startTime, endTime, spansNextDay}], blocked }]
// The wizard-style formatter (formatAvailabilitySummary above) expects:
//   { allDay, schedule: [{days: ['mon','tue',...], blocks: [{start, end}]}] }
// These helpers bridge the two so the driver card can reuse the same
// "Mon–Fri: 9 AM–5 PM" collapsing logic, then append the property's
// timezone abbreviation to each line.

const DAY_NAME_TO_TOKEN = {
    Monday: 'mon', Tuesday: 'tue', Wednesday: 'wed', Thursday: 'thu',
    Friday: 'fri', Saturday: 'sat', Sunday: 'sun',
};

/**
 * Invert scheduleBlocksToRecurringDays from the server. Groups days whose
 * time slots are identical into a single entry so the downstream summary
 * collapses cleanly (one "Mon–Fri" line instead of five "Monday/Tuesday"
 * lines). Blocked days are dropped (they don't appear in the summary).
 */
export function recurringDaysToScheduleBlocks(recurringDays) {
    if (!Array.isArray(recurringDays) || recurringDays.length === 0) {
        return { allDay: false, schedule: [] };
    }
    const groups = new Map();
    for (const rd of recurringDays) {
        if (!rd || rd.blocked) continue;
        const dayToken = DAY_NAME_TO_TOKEN[rd.dayOfWeek];
        if (!dayToken) continue;
        if (!Array.isArray(rd.timeSlots) || rd.timeSlots.length === 0) continue;
        const blocks = rd.timeSlots
            .filter((ts) => ts && ts.startTime && ts.endTime)
            .map((ts) => ({ start: ts.startTime, end: ts.endTime }));
        if (blocks.length === 0) continue;
        const key = JSON.stringify(blocks);
        if (!groups.has(key)) groups.set(key, { days: [], blocks });
        groups.get(key).days.push(dayToken);
    }
    const schedule = Array.from(groups.values())
        .map((g) => ({
            days: g.days.slice().sort((a, b) => DAY_TOKENS.indexOf(a) - DAY_TOKENS.indexOf(b)),
            blocks: g.blocks,
        }));

    // Detect 24/7: every day + a single block that spans the whole day.
    // The server's allDayRecurringDays() emits 00:00 → 23:59, so match
    // that plus the equivalent 00:00 → 00:00 (same-time = full day in
    // expandBlockIntervals).
    if (schedule.length === 1 && schedule[0].days.length === DAY_TOKENS.length) {
        const b = schedule[0].blocks[0];
        const isFullDayBlock = schedule[0].blocks.length === 1 && b && (
            (b.start === '00:00' && (b.end === '23:59' || b.end === '00:00'))
            || b.start === b.end
        );
        if (isFullDayBlock) return { allDay: true, schedule: [] };
    }

    return { allDay: false, schedule };
}

/**
 * Returns a short timezone label (e.g. "CST", "EDT", "PST") for the
 * given IANA zone. Uses Intl.DateTimeFormat with timeZoneName: 'short',
 * which on modern engines yields an abbreviation when one exists or
 * a GMT offset (e.g. "GMT+5") when it doesn't. Falls back to the IANA
 * name if Intl isn't usable for the given zone.
 */
export function formatTimezoneAbbrev(ianaTimeZone, when = new Date()) {
    if (!ianaTimeZone || typeof ianaTimeZone !== 'string') return '';
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: ianaTimeZone,
            timeZoneName: 'short',
        }).formatToParts(when);
        const tz = parts.find((p) => p.type === 'timeZoneName')?.value;
        return tz || ianaTimeZone;
    } catch {
        return ianaTimeZone;
    }
}

/**
 * Driver-facing one-shot: takes the server's Availability.recurringDays
 * plus the IANA timezone and returns ready-to-render lines like:
 *   "Mon–Fri: 9 AM–5 PM CST"
 *   "Sat–Sun: 10 AM–2 PM CST"
 * Or for a 24/7 property:
 *   "Open 24/7 CST"
 * Returns [] when there's nothing to render (no doc, all-blocked, etc.).
 */
export function formatDriverAvailability(recurringDays, timeZone) {
    const wizardShape = recurringDaysToScheduleBlocks(recurringDays);
    const lines = formatAvailabilitySummary(wizardShape);
    if (lines.length === 0) return [];
    const tz = formatTimezoneAbbrev(timeZone);
    if (!tz) return lines;
    return lines.map((line) => `${line} ${tz}`);
}
